import { Brain } from 'lucide-react';
import BackToHome from './BackToHome';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <BackToHome />
            <div className="flex items-center gap-2">
              <div className="bg-[#8CAFA8] text-white p-2 rounded-lg">
                <Brain className="w-5 h-5" />
              </div>
              <span className="text-xl font-semibold text-[#1F2937]">Mentra</span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white rounded-[24px] shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-semibold text-[#1F2937] mb-2">Website Terms of Use</h1>
          <p className="text-[#6B7280] mb-8">Last Updated: 18 November 2025</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">1. Application of Terms</h2>
              <p className="leading-relaxed mb-3">
                <strong>1.1</strong> These Terms apply to your use of the Website. By accessing and using the Website:
              </p>
              <ul className="list-none pl-6 space-y-2 mb-6">
                <li className="leading-relaxed"><strong>a.</strong> you agree to these Terms; and</li>
                <li className="leading-relaxed">
                  <strong>b.</strong> where your access or use is on behalf of another person (e.g., a company), you confirm that you are authorised to, and do in fact, agree to these Terms on that person's behalf, and that the person is bound by these Terms.
                </li>
              </ul>
              <p className="leading-relaxed">
                <strong>1.2</strong> If you do not agree to these Terms, you are not authorised to access or use the Website and must stop immediately.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">2. Changes</h2>
              <p className="leading-relaxed mb-3">
                <strong>2.1</strong> We may change these Terms at any time by updating them on the Website. Unless stated otherwise, changes take effect immediately. You are responsible for reviewing the latest Terms. Your continued use of the Website indicates your acceptance of the updated Terms.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>2.2</strong> We may change, suspend, discontinue, or restrict access to the Website at any time without notice or liability.
              </p>
              <p className="leading-relaxed">
                <strong>2.3</strong> These Terms were last updated on 23 November 2024.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">3. Definitions</h2>
              <p className="leading-relaxed mb-3">In these Terms:</p>
              <div className="pl-6 space-y-4">
                <p className="leading-relaxed">
                  <strong>including</strong> and similar words do not imply any limit.
                </p>
                <p className="leading-relaxed">
                  <strong>Loss</strong> includes loss of profits, savings, revenue, or data, and any other claim, damage, liability, cost, or expense (including legal costs on a solicitor and own client basis).
                </p>
                <p className="leading-relaxed">
                  <strong>personal information</strong> means information about an identifiable, living person.
                </p>
                <p className="leading-relaxed">
                  <strong>Terms</strong> means these Website Terms of Use.
                </p>
                <p className="leading-relaxed">
                  <strong>Underlying System</strong> means any network, system, software, data, or material that underlies or supports the Website.
                </p>
                <p className="leading-relaxed">
                  <strong>User ID</strong> means a unique username and/or password allocated to you to access certain Website features.
                </p>
                <p className="leading-relaxed">
                  <strong>We, us, or our</strong> means Mentra.
                </p>
                <p className="leading-relaxed">
                  <strong>Website</strong> means{' '}
                  <a href="https://getmentra.com" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                    https://getmentra.com
                  </a>
                </p>
                <p className="leading-relaxed">
                  <strong>You</strong> means you, or if clause 1.1(b) applies, both you and the person on whose behalf you are acting.
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">4. Your Obligations</h2>
              <p className="leading-relaxed mb-3">
                <strong>4.1</strong> You must provide true, current, and complete information when interacting with us (including when creating an account) and update it promptly if it changes.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>4.2</strong> If you are provided with a User ID, you must keep it secure and:
              </p>
              <ul className="list-none pl-6 space-y-2 mb-6">
                <li className="leading-relaxed"><strong>a.</strong> not permit any other person to use it; and</li>
                <li className="leading-relaxed">
                  <strong>b.</strong> notify us immediately of any unauthorised use or disclosure by emailing{' '}
                  <a href="mailto:team@getmentra.com" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                    team@getmentra.com
                  </a>
                  .
                </li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>4.3</strong> You must not:
              </p>
              <ul className="list-none pl-6 space-y-2 mb-6">
                <li className="leading-relaxed">
                  <strong>a.</strong> use, introduce, or attempt to introduce anything (including viruses, worms, Trojan horses, timebombs, spyware, or similar) that may compromise, damage, or interfere with the Website or any Underlying System; or
                </li>
                <li className="leading-relaxed">
                  <strong>b.</strong> access the Website using anything other than standard web browsers unless we agree otherwise. Prohibited methods include scraping, deep-linking, harvesting, data mining, robots, spiders, automation, or similar techniques.
                </li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>4.4</strong> You must obtain our written permission before linking to the Website. Requests can be sent to{' '}
                <a href="mailto:team@getmentra.com" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                  team@getmentra.com
                </a>
                .
              </p>
              <p className="leading-relaxed">
                <strong>4.5</strong> You indemnify us against all Loss arising from your failure to comply with these Terms, including Loss caused by anyone accessing the Website using your User ID.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">5. Intellectual Property</h2>
              <p className="leading-relaxed">
                We (and our licensors) own all proprietary and intellectual property rights in the Website and all content on it (including text, graphics, artwork, photographs, logos, videos, and look and feel), as well as the Underlying Systems.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">6. Disclaimers</h2>
              <p className="leading-relaxed mb-3">
                <strong>6.1</strong> To the extent permitted by law, we and our licensors are not liable for any Loss arising from or connected to:
              </p>
              <ul className="list-none pl-6 space-y-2 mb-6">
                <li className="leading-relaxed">
                  <strong>a.</strong> the Website being unavailable or performing slowly;
                </li>
                <li className="leading-relaxed">
                  <strong>b.</strong> any error or omission in information on the Website;
                </li>
                <li className="leading-relaxed">
                  <strong>c.</strong> exposure to viruses or other harmful features that may damage your device or expose you to fraud; or
                </li>
                <li className="leading-relaxed">
                  <strong>d.</strong> any third-party site linked from the Website. Links do not constitute our endorsement or responsibility.
                </li>
              </ul>
              <p className="leading-relaxed">
                <strong>6.2</strong> We make no representation that the Website is appropriate or available in all countries, or that its content complies with all laws. You are responsible for ensuring that your access and use is lawful and compliant with local requirements.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">7. Liability</h2>
              <p className="leading-relaxed mb-3">
                <strong>7.1</strong> To the maximum extent permitted by law:
              </p>
              <ul className="list-none pl-6 space-y-2 mb-6">
                <li className="leading-relaxed">
                  <strong>a.</strong> you access and use the Website at your own risk; and
                </li>
                <li className="leading-relaxed">
                  <strong>b.</strong> we are not liable for any Loss arising under or in connection with these Terms, the Website, or your access or use of (or inability to access or use) the Website, whether in contract, tort (including negligence), equity, or otherwise.
                </li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>7.2</strong> Nothing in these Terms contracts out of the New Zealand Consumer Guarantees Act 1993 or other non-excludable consumer laws. Where liability cannot be excluded but can be limited, our liability is limited to NZD$100.
              </p>
              <p className="leading-relaxed">
                <strong>7.3</strong> To the maximum extent permitted by law, and only to the extent clauses 7.1 and 7.2 do not apply, our total liability to you under or in connection with these Terms or the Website must not exceed NZD$100.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">8. Privacy Policy</h2>
              <p className="leading-relaxed mb-3">
                <strong>8.1</strong> You do not have to provide personal information, but if you do not, some Website features may not be available (e.g., receiving updates).
              </p>
              <p className="leading-relaxed mb-3">
                <strong>8.2</strong> When you provide personal information, we comply with the New Zealand Privacy Act 2020.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>8.3</strong> Personal information may be used for communication, statistical analysis, marketing of our products and services, credit checks (if needed), and research and development.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>8.4</strong> We may collect technical information about how users access, browse, and interact with the Website, including via cookies. You may disable cookies through your browser, but this may limit Website functionality.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>8.5</strong> We generally do not disclose personal information to third parties except:
              </p>
              <ul className="list-none pl-6 space-y-2 mb-6">
                <li className="leading-relaxed">
                  <strong>a.</strong> to service providers assisting with hosting or improving the Website;
                </li>
                <li className="leading-relaxed">
                  <strong>b.</strong> in relation to the sale or acquisition of our business or assets; or
                </li>
                <li className="leading-relaxed">
                  <strong>c.</strong> where required by law or valid law enforcement request.
                </li>
              </ul>
              <p className="leading-relaxed mb-3">
                <strong>8.6</strong> Personal information may be stored on secure servers of our trusted service providers, including outside New Zealand. These jurisdictions may offer lower levels of legal privacy protection.
              </p>
              <p className="leading-relaxed">
                <strong>8.7</strong> You may request access to or correction of your personal information by emailing{' '}
                <a href="mailto:team@getmentra.com" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                  team@getmentra.com
                </a>
                . For more details, see our{' '}
                <a href="/privacy" className="text-[#8CAFA8] hover:text-[#7A9B94]">
                  Privacy Policy
                </a>
                .
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">9. Suspension and Termination</h2>
              <p className="leading-relaxed mb-3">
                <strong>9.1</strong> If we believe you have breached these Terms or consider it otherwise appropriate, we may immediately suspend or terminate your access to the Website without notice.
              </p>
              <p className="leading-relaxed">
                <strong>9.2</strong> Upon suspension or termination, you must stop using the Website and not attempt further access.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-4">10. General</h2>
              <p className="leading-relaxed mb-3">
                <strong>10.1</strong> If we need to contact you, we may do so via email or by posting a notice on the Website. You agree this satisfies all legal requirements for written communication.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>10.2</strong> These Terms and any dispute relating to them are governed by New Zealand law. You submit to the non-exclusive jurisdiction of the New Zealand courts.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>10.3</strong> A waiver must be in writing to be effective.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>10.4</strong> Clauses intended to survive termination (including clauses 4.5, 5, 6, 7, and 10.1) remain in force.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>10.5</strong> If any part of these Terms becomes illegal, unenforceable, or invalid, it will be modified or severed to the minimum extent required, and the rest of the Terms remain effective.
              </p>
              <p className="leading-relaxed">
                <strong>10.6</strong> These Terms constitute the entire agreement between the parties regarding your use of the Website and replace all prior discussions or agreements. No prior representations or understandings have any effect unless expressly included in these Terms.
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="container mx-auto px-6 text-center text-[#6B7280]">
          <p>&copy; 2025 Mentra. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
